"use strict";
/**
 * Vector Tracing Asset Generators for EduWrite.
 *
 * Provides self-contained, crisp SVG Data URIs for:
 * 1. Letter Tracing Templates (EYFS / KS1)
 * 2. Shape & Landmark Tracing Templates (EYFS Geography, Art, Science, etc.)
 *
 * All SVGs are inline Data URIs (`data:image/svg+xml;utf8,...`) that load
 * instantly, work completely offline, require no external CDN/MinIO,
 * and render with 100% vector clarity on retina and high-DPI screens.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeSvgDataUri = makeSvgDataUri;
exports.getLetterTracingSvg = getLetterTracingSvg;
exports.getShapeTracingSvg = getShapeTracingSvg;
function makeSvgDataUri(svgContent) {
    const cleaned = svgContent
        .replace(/\n\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return `data:image/svg+xml;utf8,${encodeURIComponent(cleaned)}`;
}
function getLetterTracingSvg(letter) {
    const char = letter.trim().toUpperCase() || 'A';
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">
  <rect width="196" height="196" x="2" y="2" rx="24" fill="#FFFFFF" stroke="#E2E8F0" stroke-width="2"/>
  <!-- Guidelines (ascender, midline, baseline) -->
  <line x1="20" y1="40" x2="180" y2="40" stroke="#E2E8F0" stroke-width="1.5" stroke-dasharray="4 4"/>
  <line x1="20" y1="90" x2="180" y2="90" stroke="#CBD5E1" stroke-width="1.5" stroke-dasharray="4 4"/>
  <line x1="20" y1="150" x2="180" y2="150" stroke="#94A3B8" stroke-width="2"/>
  <!-- Tracing Letter (dotted glyph) -->
  <text x="100" y="145" font-family="'Nunito', 'Comic Sans MS', system-ui, sans-serif" font-size="120" font-weight="700" fill="none" stroke="#6366F1" stroke-width="4" stroke-dasharray="6 6" text-anchor="middle">${char}</text>
  <!-- Starting point indicator -->
  <circle cx="65" cy="55" r="5" fill="#22C55E"/>
</svg>`;
    return makeSvgDataUri(svg);
}
function getShapeTracingSvg(shape) {
    const key = shape.toLowerCase().trim();
    let innerContent = '';
    switch (key) {
        case 'lighthouse':
            innerContent = `
  <!-- Coastal Waves -->
  <path d="M10 185 Q30 175 50 185 T90 185 T130 185 T170 185 T200 185" fill="none" stroke="#38BDF8" stroke-width="3" stroke-dasharray="4 4"/>
  <path d="M10 192 Q30 182 50 192 T90 192 T130 192 T170 192 T200 192" fill="none" stroke="#0284C7" stroke-width="3" stroke-dasharray="4 4"/>
  <!-- Rocky Base -->
  <path d="M50 180 L150 180 L140 165 L60 165 Z" fill="none" stroke="#64748B" stroke-width="3" stroke-dasharray="5 5"/>
  <!-- Lighthouse Tower -->
  <path d="M68 165 L78 65 L122 65 L132 165 Z" fill="none" stroke="#0284C7" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- Stripe bands -->
  <line x1="72" y1="135" x2="128" y2="135" stroke="#EF4444" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="75" y1="105" x2="125" y2="105" stroke="#0284C7" stroke-width="4" stroke-dasharray="5 5"/>
  <!-- Gallery & Lantern Room -->
  <rect x="72" y="52" width="56" height="13" rx="3" fill="none" stroke="#0284C7" stroke-width="3" stroke-dasharray="5 5"/>
  <rect x="80" y="32" width="40" height="20" fill="none" stroke="#0284C7" stroke-width="3" stroke-dasharray="5 5"/>
  <!-- Roof Dome & Finial -->
  <path d="M80 32 Q100 12 120 32 Z" fill="none" stroke="#EF4444" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="100" y1="12" x2="100" y2="4" stroke="#EF4444" stroke-width="3"/>
  <!-- Beacon Light Rays -->
  <line x1="60" y1="38" x2="35" y2="28" stroke="#F59E0B" stroke-width="3" stroke-dasharray="4 4"/>
  <line x1="60" y1="46" x2="30" y2="48" stroke="#F59E0B" stroke-width="3" stroke-dasharray="4 4"/>
  <line x1="140" y1="38" x2="165" y2="28" stroke="#F59E0B" stroke-width="3" stroke-dasharray="4 4"/>
  <line x1="140" y1="46" x2="170" y2="48" stroke="#F59E0B" stroke-width="3" stroke-dasharray="4 4"/>
  <!-- Green start dot -->
  <circle cx="78" cy="65" r="5" fill="#22C55E"/>
  <text x="78" y="80" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E">START</text>`;
            break;
        case 'tree':
            innerContent = `
  <!-- Tree Trunk -->
  <path d="M85 185 L85 110 L115 110 L115 185 Z" fill="none" stroke="#92400E" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- Tree Roots -->
  <path d="M85 185 Q75 192 65 195" fill="none" stroke="#92400E" stroke-width="3" stroke-dasharray="4 4"/>
  <path d="M115 185 Q125 192 135 195" fill="none" stroke="#92400E" stroke-width="3" stroke-dasharray="4 4"/>
  <!-- Big Leafy Canopy -->
  <path d="M60 120 C35 110 30 75 55 55 C45 30 75 15 100 22 C125 15 155 30 145 55 C170 75 165 110 140 120 C130 135 70 135 60 120 Z" fill="none" stroke="#16A34A" stroke-width="5" stroke-dasharray="7 7"/>
  <!-- Leaves texture inside -->
  <circle cx="80" cy="60" r="12" fill="none" stroke="#22C55E" stroke-width="2" stroke-dasharray="4 4"/>
  <circle cx="120" cy="60" r="12" fill="none" stroke="#22C55E" stroke-width="2" stroke-dasharray="4 4"/>
  <circle cx="100" cy="85" r="14" fill="none" stroke="#22C55E" stroke-width="2" stroke-dasharray="4 4"/>
  <!-- Green start dot -->
  <circle cx="100" cy="22" r="5" fill="#22C55E"/>
  <text x="100" y="38" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E" text-anchor="middle">START</text>`;
            break;
        case 'path_to_school':
            innerContent = `
  <!-- Home House (Left) -->
  <g transform="translate(10, 130)">
    <rect x="0" y="20" width="36" height="30" rx="3" fill="#FEE2E2" stroke="#EF4444" stroke-width="2"/>
    <polygon points="18,0 38,20 -2,20" fill="#FECACA" stroke="#EF4444" stroke-width="2"/>
    <rect x="13" y="32" width="10" height="18" fill="#B91C1C"/>
    <text x="18" y="62" font-family="sans-serif" font-size="10" font-weight="bold" fill="#B91C1C" text-anchor="middle">HOME</text>
  </g>
  <!-- School Building (Right) -->
  <g transform="translate(145, 15)">
    <rect x="0" y="20" width="45" height="35" rx="3" fill="#E0E7FF" stroke="#4F46E5" stroke-width="2"/>
    <polygon points="22,0 48,20 -3,20" fill="#C7D2FE" stroke="#4F46E5" stroke-width="2"/>
    <circle cx="22" cy="30" r="6" fill="#FDE047" stroke="#CA8A04" stroke-width="1.5"/>
    <text x="22" y="66" font-family="sans-serif" font-size="9" font-weight="bold" fill="#4F46E5" text-anchor="middle">SCHOOL</text>
  </g>
  <!-- Trees and Grass Along the Way -->
  <circle cx="85" cy="130" r="10" fill="#DCFCE7" stroke="#16A34A" stroke-width="1.5" stroke-dasharray="3 3"/>
  <circle cx="115" cy="50" r="10" fill="#DCFCE7" stroke="#16A34A" stroke-width="1.5" stroke-dasharray="3 3"/>
  <!-- Winding Path from Home to School -->
  <path d="M48 165 C85 165 65 105 100 100 C135 95 110 50 148 45" fill="none" stroke="#4F46E5" stroke-width="5" stroke-dasharray="8 6"/>
  <!-- Zebra stripes / Footprints on path -->
  <line x1="68" y1="160" x2="74" y2="152" stroke="#F59E0B" stroke-width="3"/>
  <line x1="88" y1="118" x2="94" y2="110" stroke="#F59E0B" stroke-width="3"/>
  <line x1="120" y1="78" x2="126" y2="70" stroke="#F59E0B" stroke-width="3"/>
  <!-- Green start dot at home -->
  <circle cx="48" cy="165" r="6" fill="#22C55E"/>
  <text x="48" y="180" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E">START</text>`;
            break;
        case 'house':
            innerContent = `
  <!-- House base -->
  <rect x="45" y="85" width="110" height="90" rx="4" fill="none" stroke="#E11D48" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- Roof -->
  <polygon points="100,25 165,85 35,85" fill="none" stroke="#E11D48" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- Chimney -->
  <path d="M130 52 L130 35 L145 35 L145 66" fill="none" stroke="#E11D48" stroke-width="3" stroke-dasharray="4 4"/>
  <!-- Door -->
  <rect x="85" y="125" width="30" height="50" rx="3" fill="none" stroke="#0284C7" stroke-width="3" stroke-dasharray="4 4"/>
  <circle cx="110" cy="150" r="2.5" fill="#0284C7"/>
  <!-- Windows -->
  <rect x="58" y="100" width="22" height="22" fill="none" stroke="#F59E0B" stroke-width="2.5" stroke-dasharray="4 4"/>
  <rect x="120" y="100" width="22" height="22" fill="none" stroke="#F59E0B" stroke-width="2.5" stroke-dasharray="4 4"/>
  <!-- Green start dot -->
  <circle cx="100" cy="25" r="5" fill="#22C55E"/>
  <text x="100" y="42" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E" text-anchor="middle">START</text>`;
            break;
        case 'leaf':
            innerContent = `
  <!-- Leaf outline -->
  <path d="M100 20 C150 50 160 130 100 170 C40 130 50 50 100 20 Z" fill="none" stroke="#16A34A" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- Central stem -->
  <path d="M100 20 L100 190" fill="none" stroke="#15803D" stroke-width="3" stroke-dasharray="5 5"/>
  <!-- Side veins -->
  <path d="M100 60 L130 80 M100 60 L70 80" fill="none" stroke="#16A34A" stroke-width="2.5" stroke-dasharray="4 4"/>
  <path d="M100 100 L135 120 M100 100 L65 120" fill="none" stroke="#16A34A" stroke-width="2.5" stroke-dasharray="4 4"/>
  <path d="M100 140 L125 155 M100 140 L75 155" fill="none" stroke="#16A34A" stroke-width="2.5" stroke-dasharray="4 4"/>
  <!-- Green start dot -->
  <circle cx="100" cy="20" r="5" fill="#22C55E"/>
  <text x="100" y="36" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E" text-anchor="middle">START</text>`;
            break;
        case 'star':
            innerContent = `
  <!-- 5-Pointed Star -->
  <polygon points="100,20 123,75 182,75 134,110 152,165 100,132 48,165 66,110 18,75 77,75" fill="none" stroke="#F59E0B" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- Green start dot -->
  <circle cx="100" cy="20" r="5" fill="#22C55E"/>
  <text x="100" y="38" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E" text-anchor="middle">START</text>`;
            break;
        case 'sun':
            innerContent = `
  <!-- Sun Core -->
  <circle cx="100" cy="100" r="45" fill="none" stroke="#F59E0B" stroke-width="4" stroke-dasharray="6 6"/>
  <!-- 8 Sun Rays -->
  <line x1="100" y1="45" x2="100" y2="15" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="100" y1="155" x2="100" y2="185" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="45" y1="100" x2="15" y2="100" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="155" y1="100" x2="185" y2="100" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="61" y1="61" x2="40" y2="40" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="139" y1="139" x2="160" y2="160" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="139" y1="61" x2="160" y2="40" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <line x1="61" y1="139" x2="40" y2="160" stroke="#EA580C" stroke-width="4" stroke-dasharray="5 5"/>
  <!-- Green start dot -->
  <circle cx="100" cy="55" r="5" fill="#22C55E"/>
  <text x="100" y="70" font-family="sans-serif" font-size="10" font-weight="bold" fill="#22C55E" text-anchor="middle">START</text>`;
            break;
        default:
            innerContent = `
  <rect x="25" y="25" width="150" height="150" rx="20" fill="none" stroke="#6366F1" stroke-width="4" stroke-dasharray="6 6"/>
  <circle cx="25" cy="45" r="5" fill="#22C55E"/>`;
            break;
    }
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">
  <rect width="196" height="196" x="2" y="2" rx="24" fill="#FFFFFF" stroke="#E2E8F0" stroke-width="2"/>
  ${innerContent}
</svg>`;
    return makeSvgDataUri(svg);
}
